<script setup lang="ts">
import { computed } from "vue";

// Presentational step card for the anchored welcome tour. Positioning, focus, keyboard handling and
// the seen-flag live in WelcomeTour.vue; this component only renders one step and reports clicks.
const props = withDefaults(
  defineProps<{
    title: string;
    bodyHtml: string;
    stepIndex: number;
    stepCount: number;
    primaryLabel: string;
    headingId: string;
    // "lg" is the new-user deck's large centered intro, shown before the anchored steps start.
    size?: "md" | "lg";
    // Skip shows only on an on-demand replay (nav ⓘ). A real run cannot be skipped.
    skippable?: boolean;
  }>(),
  { size: "md", skippable: false }
);

const emit = defineEmits<{
  back: [];
  next: [];
  skip: [];
}>();

// "lg" is the large centered intro shown before the first deck's anchored steps start (see the
// mockup): bigger radius and padding, centered heading/body, no step counter, no footer rule.
const isLarge = computed(() => props.size === "lg");
const sizeClass = computed(() =>
  isLarge.value ? "w-[600px] max-w-[calc(100vw-2rem)] rounded-16" : "w-[380px] max-w-[calc(100vw-2rem)] rounded-8"
);
// The "lg" footer shares the content's horizontal padding and sits close under the body — the
// content's own pb-2 + the footer's pt-2 is the gap between them, not a stray border-separated bar.
const contentClass = computed(() => (isLarge.value ? "px-10 pt-10 pb-2 text-center" : "p-5"));
const headingSizeClass = computed(() => (isLarge.value ? "t-sh2" : "t-sh3"));
const footerClass = computed(() =>
  isLarge.value
    ? "flex items-center justify-end px-10 pb-8 pt-2"
    : "flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 px-4 py-3"
);

// Native token-styled buttons, matching the design-system-2.0 pattern used by the start-project
// dialog's footer (StartProjectModal.vue actionClass) and NewProjectButton, rather than the
// FontAwesome-era `Button` reusable.
const actionClass =
  "focus-ring interactive-active t-small-bold duration-fast ease-standard inline-flex items-center justify-center gap-2 rounded-8 px-4 py-2 transition-colors";
</script>

<template>
  <div :class="['bg-slate-0 shadow-dropdown flex flex-col ring-1 ring-slate-200', sizeClass]">
    <div :class="['flex flex-col gap-3', contentClass]">
      <h2
        :id="headingId"
        :class="[headingSizeClass, 'text-type-default']">
        {{ title }}
      </h2>
      <!-- Markdown paragraphs carry no text-align class of their own, so they inherit the centered
           alignment from this wrapper on the "lg" intro. -->
      <div
        class="flex flex-col gap-3"
        v-html="bodyHtml" />
    </div>

    <div :class="footerClass">
      <span
        v-if="!isLarge"
        class="t-caption text-type-muted shrink-0 whitespace-nowrap">
        Step {{ stepIndex + 1 }} of {{ stepCount }}
      </span>

      <div class="ml-auto flex gap-2">
        <!-- Ghost pattern: same ring-slate-250 quiet outline as NewProjectButton / the start dialog's
             Cancel — a secondary action on a bg-slate-0 card. -->
        <button
          v-if="stepIndex > 0"
          type="button"
          :class="[actionClass, 'bg-slate-0 text-type-default ring-slate-250 ring-1 hover:bg-slate-100 hover:ring-slate-300']"
          @click="emit('back')">
          Back
        </button>
        <!-- Quiet text action, no fill or ring: Skip is a way out, not a step forward, so it reads
             lighter than Back. -->
        <button
          v-if="skippable"
          type="button"
          :class="[actionClass, 'text-type-muted hover:text-type-default']"
          @click="emit('skip')">
          Skip
        </button>
        <button
          type="button"
          :class="[
            actionClass,
            'bg-app-blue-600 dark:bg-app-blue-fill hover:bg-app-blue-700 dark:hover:bg-app-blue-fill-hover whitespace-nowrap text-white',
          ]"
          @click="emit('next')">
          {{ primaryLabel }}
        </button>
      </div>
    </div>
  </div>
</template>
